import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../../lib/api";

/**
 * Sequence metrics from API
 */
interface SequenceMetrics {
  name: string;
  enrolled: number;
  active: number;
  paused: number;
  completed: number;
  exited: number;
  completionRate: number;
  avgPosition: number;
}

interface SequencesResponse {
  sequences: SequenceMetrics[];
}

/**
 * EmailSequencesPage - Admin page for managing email sequences.
 *
 * Features:
 * - List all sequences with enrollment counts
 * - View sequence completion rates
 * - See conversion metrics per sequence
 */
export function EmailSequencesPage() {
  const [sequences, setSequences] = useState<SequenceMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const res = await apiGet<SequencesResponse>("/api/admin/emails/sequences");

        if (res.error) {
          throw new Error(res.error.message);
        }

        setSequences(res.data?.sequences ?? []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load sequences");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  // Calculate totals
  const totals = sequences.reduce(
    (acc, seq) => ({
      enrolled: acc.enrolled + seq.enrolled,
      active: acc.active + seq.active,
      paused: acc.paused + seq.paused,
      completed: acc.completed + seq.completed,
      exited: acc.exited + seq.exited,
    }),
    { enrolled: 0, active: 0, paused: 0, completed: 0, exited: 0 }
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-magic-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600 font-medium">Error loading sequences</p>
        <p className="text-red-500 text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Sequences</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage and monitor automated email sequences
          </p>
        </div>
        <Link
          to="/admin/emails"
          className="
            px-4 py-2 text-sm font-medium
            text-gray-700 bg-white border border-gray-200 rounded-lg
            hover:bg-gray-50 transition-colors inline-flex items-center gap-2
          "
        >
          <ArrowLeftIcon />
          Back to Email Dashboard
        </Link>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <SummaryCard
          title="Total Enrolled"
          value={totals.enrolled}
          color="gray"
        />
        <SummaryCard
          title="Active"
          value={totals.active}
          color="green"
        />
        <SummaryCard
          title="Paused"
          value={totals.paused}
          color="yellow"
        />
        <SummaryCard
          title="Completed"
          value={totals.completed}
          color="blue"
        />
        <SummaryCard
          title="Exited"
          value={totals.exited}
          color="red"
        />
      </div>

      {/* Sequences List */}
      {sequences.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <SequenceIcon />
          <p className="mt-4 text-gray-600 font-medium">No sequences found</p>
          <p className="mt-1 text-sm text-gray-500">
            Email sequences will appear here once users are enrolled
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sequences.map((seq) => (
            <SequenceCard key={seq.name} sequence={seq} />
          ))}
        </div>
      )}
    </div>
  );
}

// Components

interface SummaryCardProps {
  title: string;
  value: number;
  color: "gray" | "green" | "yellow" | "blue" | "red";
}

function SummaryCard({ title, value, color }: SummaryCardProps) {
  const colors = {
    gray: "bg-gray-50 border-gray-200",
    green: "bg-green-50 border-green-200",
    yellow: "bg-yellow-50 border-yellow-200",
    blue: "bg-blue-50 border-blue-200",
    red: "bg-red-50 border-red-200",
  };

  const textColors = {
    gray: "text-gray-900",
    green: "text-green-700",
    yellow: "text-yellow-700",
    blue: "text-blue-700",
    red: "text-red-700",
  };

  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
        {title}
      </p>
      <p className={`mt-1 text-2xl font-bold ${textColors[color]}`}>
        {value.toLocaleString()}
      </p>
    </div>
  );
}

interface SequenceCardProps {
  sequence: SequenceMetrics;
}

function SequenceCard({ sequence }: SequenceCardProps) {
  const completionPct = Math.round(sequence.completionRate * 100);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {formatSequenceName(sequence.name)}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Avg. position: {sequence.avgPosition.toFixed(1)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-magic-600">{completionPct}%</p>
          <p className="text-xs text-gray-500">completion rate</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-magic-500 to-magic-600 rounded-full transition-all"
            style={{ width: `${completionPct}%` }}
          />
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-5 gap-3 text-center">
        <StatItem
          label="Enrolled"
          value={sequence.enrolled}
          color="gray"
        />
        <StatItem
          label="Active"
          value={sequence.active}
          color="green"
        />
        <StatItem
          label="Paused"
          value={sequence.paused}
          color="yellow"
        />
        <StatItem
          label="Completed"
          value={sequence.completed}
          color="blue"
        />
        <StatItem
          label="Exited"
          value={sequence.exited}
          color="red"
        />
      </div>

      {/* Funnel visualization */}
      {sequence.enrolled > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-500 mb-2">Funnel</p>
          <div className="flex items-center gap-2">
            <FunnelStep
              label="Start"
              value={sequence.enrolled}
              total={sequence.enrolled}
            />
            <FunnelArrow />
            <FunnelStep
              label="Active/Paused"
              value={sequence.active + sequence.paused}
              total={sequence.enrolled}
            />
            <FunnelArrow />
            <FunnelStep
              label="Complete"
              value={sequence.completed}
              total={sequence.enrolled}
            />
          </div>
        </div>
      )}
    </div>
  );
}

interface StatItemProps {
  label: string;
  value: number;
  color: "gray" | "green" | "yellow" | "blue" | "red";
}

function StatItem({ label, value, color }: StatItemProps) {
  const textColors = {
    gray: "text-gray-900",
    green: "text-green-600",
    yellow: "text-yellow-600",
    blue: "text-blue-600",
    red: "text-red-600",
  };

  return (
    <div>
      <p className={`text-lg font-semibold ${textColors[color]}`}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}

interface FunnelStepProps {
  label: string;
  value: number;
  total: number;
}

function FunnelStep({ label, value, total }: FunnelStepProps) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div className="flex-1 text-center">
      <div className="bg-gray-100 rounded-lg py-2 px-3">
        <p className="text-sm font-semibold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500">{pct}%</p>
      </div>
      <p className="text-xs text-gray-400 mt-1">{label}</p>
    </div>
  );
}

function FunnelArrow() {
  return (
    <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
    </svg>
  );
}

// Helper functions
function formatSequenceName(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Icons
function ArrowLeftIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function SequenceIcon() {
  return (
    <svg className="w-12 h-12 text-gray-300 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}

export default EmailSequencesPage;
